import React from 'react';
import Image from 'next/image';
import { cn } from '@/utils';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

type ImageOrSvgProps = {
  url: string;
  alt?: string;
  width?: string | number;
  height?: string | number;
  classNameSvg?: string;
};

export const ImageOrSvg: React.FC<ImageOrSvgProps> = ({
  url,
  alt = 'Image',
  width = 'auto',
  height = 'auto',
  classNameSvg,
}) => {
  // Usamos useQuery para gestionar la consulta al recurso.
  const { data, isLoading, isError } = useQuery({
    queryKey: ['imageOrSvg', url],
    queryFn: async () => {
      // Realizamos la petición a la URL proporcionada.
      const response = await axios.get(url, {
        timeout: 5000,
      });
      // Verificamos si el contenido contiene la etiqueta <svg.
      if (response.data.includes('<svg')) {
        return { isSvg: true, svgData: response.data };
      }
      return { isSvg: false, svgData: null };
    },
  });

  // En caso de error, mostramos un mensaje informativo.
  if (isError) {
    return <div></div>;
  }

  // Indicador de carga mientras se realiza la consulta.
  if (isLoading) {
    return <div></div>;
  }

  // En caso de que la consulta se haya completado pero no se determine el recurso.
  if (!data) {
    return <div></div>;
  }

  // Si el recurso es un SVG, lo renderizamos utilizando dangerouslySetInnerHTML,
  // reemplazando los atributos fill para que adopten el color actual.
  if (data.isSvg) {
    return (
      <div
        className={cn(
          'fill-primary text-primary [&>svg>g]:text-primary [&>svg]:size-full [&_*]:!fill-primary',
          classNameSvg
        )}
        style={{ width, height }}
        dangerouslySetInnerHTML={{
          __html: data.svgData?.replace(/fill="[^"]*"/g, `fill="currentColor"`) || '',
        }}
      />
    );
  }

  // Si no es un SVG, renderizamos usando next/image para optimización automática
  // Usamos unoptimized para data URIs o cuando el dominio no está configurado
  const isDataUri = url?.startsWith('data:');
  const numWidth = typeof width === 'number' ? width : 100;
  const numHeight = typeof height === 'number' ? height : 100;

  return (
    <Image
      src={url}
      alt={alt}
      width={numWidth}
      height={numHeight}
      unoptimized={isDataUri}
      style={{
        width: typeof width === 'string' ? width : undefined,
        height: typeof height === 'string' ? height : undefined,
      }}
    />
  );
};
